// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSelector } from '@reduxjs/toolkit';
import { orderStatusCode } from 'Utils/constants';

const selectProjectList = (state) => state.projectList.list;

const selectFolderProjectList = (state) => state.folderList.projectsInFolder;

const selectProjectId = (state) => state.project.projectId;

const selectProjectInfo = createSelector([selectProjectList, selectFolderProjectList, (state) => state.auth.user, (_, project, isInsideFolder) => ({ project, isInsideFolder })], (list, folderProjectList, user, { project, isInsideFolder }) => {
  const dashboardProjectFilter = isInsideFolder ? folderProjectList.filter((o) => o._id === project._id) : list.filter((o) => o._id === project._id);
  const [dashboardProject] = dashboardProjectFilter;
  if (!dashboardProject) {
    return {
      placed: 0,
      autonomous: 0,
      cleaning: 0,
      complete: 0,
    };
  }
  const orders = [...dashboardProject.orders];

  const dashboardProjectOrders = orders.sort((order1, order2) => (order1.createdAt > order2.createdAt ? 1 : -1));
  // TODO: use orderStatusCode constants instead of hardcoded values
  const orderFinalized = dashboardProjectOrders.some((o) => o.orderStatus === 7);

  const orderStatusResult = orders.reduce((result, order) => {
    if ([1, 2, 3, 4, 5, 6, 7, 8].includes(order.orderStatus)) {
      result[order.ownerProject] = order.orderStatus;
    }
    return result;
  }, {} as { [key: string]: number });

  const orderEstimatedDeliveryDates = orders.reduce((result, order) => {
    if (order?.deliveryEstimate) {
      result[order.ownerProject] = order.deliveryEstimate;
    }
    return result;
  }, {} as { [key: string]: Date });

  const orderDeliveredDates = orders.reduce((result, order) => {
    if (order?.deliveredDate) {
      result[order.ownerProject] = order.deliveredDate;
    }
    return result;
  }, {} as { [key: string]: Date });

  const orderEstimateResult = orders.filter((order) => order.isEstimate).map((order) => order.ownerProject);

  const expressAIReady = orders?.some((order) => order.cadFiles?.some((cadfile) => (cadfile.expressAIUpload && cadfile.expressAIEmailSent)));

  // Check if any of the orders in the project are being processed
  const projectProcessing = dashboardProjectOrders.some((order) => order.orderStatus > 0);
  // Check if project is created by the current user
  const isProjectCreatedByUser = project.owner === user._id;
  // Variable to check if a project can be deleted
  const cannotDelete = projectProcessing || !isProjectCreatedByUser;
  const orderStatus = orderStatusResult[project._id];
  const orderEstimate = orderEstimateResult.includes(project._id);
  const orderEstimatedDeliveryDate = orderEstimatedDeliveryDates[project._id] || null;
  const orderDeliveredOn = orderDeliveredDates[project._id] || null;
  const { imageryRequest, rasterRequest } = project;
  return {
    isInProgress: orderStatus && (orderStatus > orderStatusCode.created && orderStatus < orderStatusCode.manualDrafting),
    placed: orderStatus && (orderStatus === orderStatusCode.processing || orderStatus === orderStatusCode.autonomousFailed) ? 1 : 0,
    autonomous: orderStatus && orderStatus === orderStatusCode.autonomousSuccessful ? 2 : 0,
    cleaning: orderStatus && (orderStatus === orderStatusCode.manualDrafting || orderStatus === orderStatusCode.usQc || orderStatus === orderStatusCode.qcComplete) ? 3 : 0,
    complete: orderStatus && orderStatus === orderStatusCode.done ? 3 : 0,
    cannotDelete,
    orderFinalized,
    expressAIReady,
    orderEstimate,
    orderStatus,
    imageryRequest,
    rasterRequest,
    orderEstimatedDeliveryDate,
    orderDeliveredOn,
  };
});

const selectAllCoordinates = createSelector(
  [selectProjectId, selectProjectList],
  (projectId, projects) => {
    const coordinatesInfo = projects
      .filter((project) => project.middlePoint)
      .map((p) => ({
        coords: [p.middlePoint.longitude, p.middlePoint.latitude],
        hovered: p._id === projectId,
      }));
    return coordinatesInfo;
  },
);

const selectHoveredCoordinates = createSelector(
  [selectProjectId, selectProjectList],
  (projectId, projects) => {
    if (projectId !== null) {
      const project = projects.filter((p) => p._id === projectId);
      if (project[0] && project[0].middlePoint) {
        const projectCoordinates = [project[0].middlePoint.longitude, project[0].middlePoint.latitude];
        return projectCoordinates;
      }
    }
    return null;
  },
);

export { selectProjectInfo, selectAllCoordinates, selectHoveredCoordinates };
